// The score "paper": three stacked, viewport-sized canvases (notation, loop,
// overlay) over a tall spacer for native scrolling (§6 rendering).

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { selectedRefs, setCursor, voiceOf, eventsAt, playedBarAt, placeNote, dragNoteToString, dragNoteToStaffPos, type EditorState } from '@simplenotation/core';
import { useStore, getState, edit } from '../state/store';
import { layoutScore } from '../engrave/layout';
import { SystemCache, loadMusicFont, paintItems, paintItem, paintSystem } from '../engrave/render';
import { clearLayoutCache } from '../engrave/layout';
import { hitTest, dropTarget, boxForRef, xAtTick, barBoxAt, systemAt, type Hit, type DropHit } from '../engrave/hitTest';
import { measureText, clearMeasureCache } from '../engrave/measure';
import type { Layout, BarBox } from '../engrave/types';
import { setLoopBars } from '../playback/player';
import { setZoom, takeZoomAnchor } from './zoom';
import { registerDropTarget, type NoteDragPayload } from './noteDrag';

export const ACCENT = '#ef6a4c';
const SELECTION_BAND = 'rgba(239,106,76,0.13)';
const PLAYHEAD = '#f08a3c';
const PLAYING = '#e2502e';

let currentLayout: Layout | null = null;
export function getCurrentLayout(): Layout | null {
  return currentLayout;
}

/** A note grabbed with the mouse: dragged within its band (system, track, staff or tab). */
interface NoteGrab {
  system: number;
  track: number;
  lane: 'staff' | 'tab';
  /** History coalescing key: the whole drag is one undo step. */
  key: string;
}

type Drag =
  | { kind: 'loopStart' | 'loopEnd' }
  | { kind: 'pending'; x: number; y: number; startRef: { bar: number; beat: number }; note: NoteGrab | null }
  | { kind: 'select'; startRef: { bar: number; beat: number } }
  | ({ kind: 'note' } & NoteGrab);

let dragSeq = 0;

function useFontReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    loadMusicFont().then(() => {
      clearMeasureCache();
      clearLayoutCache();
      setReady(true);
    });
  }, []);
  return ready;
}

export function ScoreView() {
  const score = useStore((s) => s.ed.score);
  const view = useStore((s) => s.view);
  const mode = useStore((s) => s.mode);
  const fontReady = useFontReady();
  const scrollRef = useRef<HTMLDivElement>(null);
  const notationRef = useRef<HTMLCanvasElement>(null);
  const loopRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const cacheRef = useRef(new SystemCache());
  const drawState = useRef({ scrollTop: 0, scrollLeft: 0 });
  const dropPreview = useRef<{ hit: DropHit; payload: NoteDragPayload } | null>(null);

  // Track the viewport size.
  useLayoutEffect(() => {
    const el = scrollRef.current!;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layout = useMemo(() => {
    void fontReady;
    const sp = 9 * view.zoom;
    const l = layoutScore(score, {
      width: Math.max(320, size.w),
      sp,
      mode: view.layout,
      pageWidth: 816 * view.zoom,
      pageHeight: 1056 * view.zoom,
      noteNames: view.noteNames,
      noteColors: view.noteColors,
      showTitle: view.showTitle,
      measure: measureText,
      viewTranspose: mode === 'view' ? view.viewTranspose : 0,
      impliedRests: mode === 'edit',
    });
    return l;
  }, [score, size.w, view.zoom, view.layout, view.noteNames, view.noteColors, view.showTitle, view.viewTranspose, mode, fontReady]);

  useEffect(() => {
    currentLayout = layout;
    cacheRef.current.sweep();
  }, [layout]);

  const offsetX = layout.mode === 'paged' ? Math.max(0, (size.w - layout.width) / 2) : 0;

  // ------------------------------------------------------------ zoom
  // After a zoom the systems reflow; keep the music under the anchor (the
  // pointer for wheel/pinch, else the cursor) at the same place on screen.
  const prevLayout = useRef<{ layout: Layout; offsetX: number } | null>(null);
  useLayoutEffect(() => {
    const prev = prevLayout.current;
    prevLayout.current = { layout, offsetX };
    const el = scrollRef.current;
    if (!prev || !el || prev.layout.sp === layout.sp) return;
    const { scrollTop, scrollLeft } = drawState.current;
    const at = takeZoomAnchor() ?? cursorAnchor(prev.layout, prev.offsetX, el, scrollTop, scrollLeft) ?? { x: el.clientWidth / 2, y: el.clientHeight / 3 };
    const ox = at.x + scrollLeft - prev.offsetX;
    const oy = at.y + scrollTop;
    const sys = systemAt(prev.layout, oy);
    const bb = sys && barBoxAt(prev.layout, sys, ox);
    const nb = bb && layout.bars.find((b) => b.bar === bb.bar);
    if (!sys || !bb || !nb) return;
    const fx = (ox - bb.x0) / Math.max(1, bb.x1 - bb.x0);
    const ny = layout.systems[nb.system].top + (oy - sys.top) * (layout.sp / prev.layout.sp);
    const nx = nb.x0 + fx * (nb.x1 - nb.x0);
    el.scrollTop = Math.max(0, ny - at.y);
    el.scrollLeft = Math.max(0, nx + offsetX - at.x);
    drawState.current = { scrollTop: el.scrollTop, scrollLeft: el.scrollLeft };
  }, [layout, offsetX]);

  // Ctrl/⌘ + wheel and trackpad pinch zoom the score, not the page. A native
  // listener because React's wheel handler is passive and can't preventDefault.
  useEffect(() => {
    const el = scrollRef.current!;
    let delta = 0;
    let at = { x: 0, y: 0 };
    let frame = 0;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      delta += e.deltaY * (e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 400 : 1);
      const r = el.getBoundingClientRect();
      at = { x: e.clientX - r.left, y: e.clientY - r.top };
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setZoom(getState().view.zoom * Math.exp(-delta / 600), at);
        delta = 0;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      cancelAnimationFrame(frame);
    };
  }, []);

  // ------------------------------------------------------------ painting
  const paintNotation = () => {
    const canvas = notationRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const { w, h } = size;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    const ctx = canvas.getContext('2d')!;
    const { scrollTop, scrollLeft } = drawState.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = layout.mode === 'paged' ? '#c9cacf' : '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.translate(offsetX - scrollLeft, -scrollTop);
    if (layout.mode === 'paged') {
      for (const p of layout.pages) {
        if (p.y1 < scrollTop || p.y0 > scrollTop + h) continue;
        ctx.fillStyle = 'rgba(0,0,0,0.18)';
        ctx.fillRect(3, p.y0 + 3, layout.width, p.y1 - p.y0);
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, p.y0, layout.width, p.y1 - p.y0);
      }
    }
    if (layout.header.length && scrollTop < (layout.systems[0]?.y0 ?? 0) + 20) paintItems(ctx, layout.header);
    for (const sys of layout.systems) {
      if (sys.y1 + 40 < scrollTop || sys.y0 - 40 > scrollTop + h) continue;
      if (layout.mode === 'horizontal') {
        paintSystem(ctx, sys);
        continue;
      }
      const c = cacheRef.current.get(sys, layout.width, dpr);
      ctx.drawImage(c.canvas as CanvasImageSource, 0, sys.dy + c.top, c.width, c.height);
    }
  };

  const paintOverlay = () => {
    const canvas = overlayRef.current;
    const loopCanvas = loopRef.current;
    if (!canvas || !loopCanvas) return;
    const dpr = window.devicePixelRatio || 1;
    const { w, h } = size;
    for (const c of [canvas, loopCanvas]) {
      if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
        c.width = Math.round(w * dpr);
        c.height = Math.round(h * dpr);
      }
    }
    const s = getState();
    const ed = s.ed;
    const ctx = canvas.getContext('2d')!;
    const { scrollTop, scrollLeft } = drawState.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.translate(offsetX - scrollLeft, -scrollTop);
    const sp = layout.sp;
    const playing = s.playback.playing;

    // Selection band + selected notes in the accent color.
    if (!playing || ed.selection) drawSelection(ctx, layout, ed);

    // Playhead and sounding notes.
    if (playing || s.playback.tick > 0) drawPlayhead(ctx, layout, s.playback.tick, playing);

    // Cursor.
    if (!playing && s.mode === 'edit') {
      const c = ed.cursor;
      const box = boxForRef(layout, ed.score, c);
      const sys = box ? layout.systems[box.system] : null;
      const band = sys?.bands.find((b) => b.track === c.track && b.kind === c.lane) ?? sys?.bands.find((b) => b.track === c.track);
      if (box && band) {
        const cx = box.x + box.w / 2;
        let cy: number;
        if (band.kind === 'tab') cy = band.top + (Math.min(c.string, band.lines) - 1) * band.gap;
        else {
          cy = band.top + 2 * sp - c.staffPos * (sp / 2);
          // Hint ledger lines outside the staff.
          ctx.strokeStyle = 'rgba(239,106,76,0.55)';
          ctx.lineWidth = 1;
          for (let p = 6; p <= c.staffPos; p += 2) line(ctx, cx - 0.9 * sp, band.top + 2 * sp - p * (sp / 2), cx + 0.9 * sp, band.top + 2 * sp - p * (sp / 2));
          for (let p = -6; p >= c.staffPos; p -= 2) line(ctx, cx - 0.9 * sp, band.top + 2 * sp - p * (sp / 2), cx + 0.9 * sp, band.top + 2 * sp - p * (sp / 2));
        }
        // Beat column hint.
        const top = Math.min(...sys!.bands.filter((b) => b.track === c.track).map((b) => b.top)) - sp;
        const bottom = Math.max(...sys!.bands.filter((b) => b.track === c.track).map((b) => b.bottom)) + sp;
        ctx.fillStyle = 'rgba(239,106,76,0.07)';
        ctx.fillRect(box.x - 0.35 * sp, top, box.w + 0.7 * sp, bottom - top);
        ctx.beginPath();
        ctx.arc(cx, cy, 0.78 * sp, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(239,106,76,0.38)';
        ctx.fill();
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = ACCENT;
        ctx.stroke();
      }
    }

    // Where a note dragged from the palette would land.
    if (dropPreview.current) drawDropPreview(ctx, layout, dropPreview.current.hit, dropPreview.current.payload);

    // Loop layer.
    const lctx = loopCanvas.getContext('2d')!;
    lctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    lctx.clearRect(0, 0, w, h);
    const loop = s.playback.loop;
    if (loop) {
      lctx.translate(offsetX - scrollLeft, -scrollTop);
      drawLoop(lctx, layout, loop.fromBar, loop.toBar, w + scrollLeft, scrollTop, h);
    }
  };

  // Repaint on layout/scroll/size.
  useEffect(() => {
    paintNotation();
    paintOverlay();
  });

  useEffect(() => {
    let frame = 0;
    const unsub = useStore.subscribe((s, prev) => {
      if (s.ed.cursor !== prev.ed.cursor || s.ed.selection !== prev.ed.selection || s.playback !== prev.playback || s.mode !== prev.mode) {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          paintOverlay();
          if (s.playback.playing && s.view.followPlayhead) followPlayhead(s.playback.tick);
        });
      }
      if (s.ed.cursor !== prev.ed.cursor && !s.playback.playing) requestAnimationFrame(revealCursor);
    });
    return () => {
      unsub();
      cancelAnimationFrame(frame);
    };
  });

  const onScroll = () => {
    const el = scrollRef.current!;
    drawState.current = { scrollTop: el.scrollTop, scrollLeft: el.scrollLeft };
    paintNotation();
    paintOverlay();
  };

  // ------------------------------------------------------------ scrolling helpers
  const revealCursor = () => {
    const el = scrollRef.current;
    if (!el) return;
    const ed = getState().ed;
    const box = boxForRef(layout, ed.score, ed.cursor);
    if (!box) return;
    const sys = layout.systems[box.system];
    if (!sys) return;
    if (sys.y0 < el.scrollTop) el.scrollTop = Math.max(0, sys.y0 - 20);
    else if (sys.y1 > el.scrollTop + el.clientHeight) el.scrollTop = sys.y1 - el.clientHeight + 20;
    const x = box.x + offsetX;
    if (x < el.scrollLeft + 40) el.scrollLeft = Math.max(0, x - 80);
    else if (x > el.scrollLeft + el.clientWidth - 40) el.scrollLeft = x - el.clientWidth + 120;
  };

  const followPlayhead = (tick: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const pos = playheadPos(layout, tick);
    if (!pos) return;
    const sys = layout.systems[pos.system];
    if (sys.y1 > el.scrollTop + el.clientHeight || sys.y0 < el.scrollTop) el.scrollTo({ top: Math.max(0, sys.y0 - 30), behavior: 'smooth' });
    if (layout.mode === 'horizontal') {
      const x = pos.x + offsetX;
      if (x > el.scrollLeft + el.clientWidth * 0.7 || x < el.scrollLeft) el.scrollLeft = x - el.clientWidth * 0.3;
    }
  };

  // ------------------------------------------------------------ pointer input
  // Press on a note and move up/down: drag the note (tab: to another string,
  // staff: to another line or space). Any other press-and-move selects beats.
  const drag = useRef<Drag | null>(null);

  const clientToLayout = (clientX: number, clientY: number) => {
    const rect = overlayRef.current!.getBoundingClientRect();
    const { scrollTop, scrollLeft } = drawState.current;
    return { x: clientX - rect.left - offsetX + scrollLeft, y: clientY - rect.top + scrollTop };
  };
  const toLayout = (e: React.PointerEvent) => clientToLayout(e.clientX, e.clientY);

  /** The note under the pointer (fret number on tab, notehead on the staff), if any. */
  const noteAt = (hit: Hit, p: { x: number; y: number }): { string: number; staffPos: number } | null => {
    const s = getState();
    if (s.mode !== 'edit' || !s.doc.canEdit) return null;
    const beat = voiceOf(s.ed.score, hit.ref)[hit.ref.beat];
    if (!beat || beat.rest) return null;
    const box = layout.beatIndex.get(beat.id);
    if (!box) return null;
    const sp = layout.sp;
    if (Math.abs(p.x - (box.x + box.w / 2)) > Math.max(box.w, 1.4 * sp)) return null;
    const ys = hit.lane === 'tab' ? box.tabYs : box.noteYs;
    let best = -1;
    let bestD = hit.lane === 'tab' ? hit.band.gap * 0.55 : sp * 0.6;
    ys.forEach((y, i) => {
      const d = Math.abs(y - p.y);
      if (d <= bestD) {
        bestD = d;
        best = i;
      }
    });
    const n = beat.notes[best];
    if (!n) return null;
    if (hit.lane === 'tab') return n.string ? { string: n.string, staffPos: hit.staffPos } : null;
    return { string: n.string ?? hit.string, staffPos: Math.round((2 * sp - (ys[best] - hit.band.top)) / (sp / 2)) };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const p = toLayout(e);
    const s = getState();
    // Loop handles.
    const loop = s.playback.loop;
    if (loop) {
      const handle = loopHandleAt(layout, loop.fromBar, loop.toBar, p.x, p.y);
      if (handle) {
        drag.current = { kind: handle };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        return;
      }
    }
    const hit = hitTest(layout, s.ed.score, p.x, p.y, s.ed.cursor.voice);
    if (!hit) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    scrollRef.current?.focus({ preventScroll: true });
    const note = e.shiftKey ? null : noteAt(hit, p);
    edit((ed) => setCursor(ed, hit.ref, { lane: hit.lane, string: note?.string ?? hit.string, staffPos: note?.staffPos ?? hit.staffPos, extend: e.shiftKey }), { select: true });
    drag.current = {
      kind: 'pending',
      x: p.x,
      y: p.y,
      startRef: { bar: hit.ref.bar, beat: hit.ref.beat },
      note: note ? { system: hit.system.index, track: hit.band.track, lane: hit.lane, key: `drag:${++dragSeq}` } : null,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    let d = drag.current;
    const p = toLayout(e);
    if (!d) {
      const s = getState();
      const loop = s.playback.loop;
      const over = loop ? loopHandleAt(layout, loop.fromBar, loop.toBar, p.x, p.y) : null;
      const hit = over ? null : hitTest(layout, s.ed.score, p.x, p.y, s.ed.cursor.voice);
      overlayRef.current!.style.cursor = over ? 'ew-resize' : hit && noteAt(hit, p) ? 'grab' : 'default';
      return;
    }
    if (d.kind === 'pending') {
      // Decide on the first real movement: mostly vertical on a note drags it.
      const dx = p.x - d.x;
      const dy = p.y - d.y;
      if (Math.hypot(dx, dy) < 4) return;
      d = drag.current = d.note && Math.abs(dy) > Math.abs(dx) ? { kind: 'note', ...d.note } : { kind: 'select', startRef: d.startRef };
      if (d.kind === 'note') overlayRef.current!.style.cursor = 'grabbing';
    }
    if (d.kind === 'note') {
      const band = layout.systems[d.system]?.bands.find((b) => b.track === d.track && b.kind === d.lane);
      if (!band) return;
      const opts = { label: 'Drag note', coalesce: d.key, coalesceMs: Infinity };
      if (d.lane === 'tab') {
        const string = Math.max(1, Math.min(band.lines, Math.round((p.y - band.top) / band.gap) + 1));
        const keepPitch = e.altKey;
        edit((ed) => dragNoteToString(ed, string, keepPitch), opts);
      } else {
        const sp = layout.sp;
        const pos = Math.max(-16, Math.min(16, Math.round((2 * sp - (p.y - band.top)) / (sp / 2))));
        edit((ed) => dragNoteToStaffPos(ed, pos), opts);
      }
      return;
    }
    if (d.kind === 'select') {
      const s = getState();
      const hit = hitTest(layout, s.ed.score, p.x, p.y, s.ed.cursor.voice);
      if (!hit || hit.ref.track !== s.ed.cursor.track) return;
      const st = d.startRef;
      if (hit.ref.bar === st.bar && hit.ref.beat === st.beat && !s.ed.selection) return;
      edit(
        (ed) => {
          const next = setCursor(ed, hit.ref, { lane: hit.lane, string: hit.string, staffPos: hit.staffPos });
          return { ...next, selection: { anchor: st, focus: { bar: hit.ref.bar, beat: hit.ref.beat } } };
        },
        { select: true },
      );
      return;
    }
    const sys = systemAt(layout, p.y);
    if (!sys) return;
    const bb = barBoxAt(layout, sys, p.x);
    const loop = getState().playback.loop;
    if (!bb || !loop) return;
    if (d.kind === 'loopStart') setLoopBars(Math.min(bb.bar, loop.toBar), loop.toBar);
    else setLoopBars(loop.fromBar, Math.max(bb.bar, loop.fromBar));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (drag.current?.kind === 'note') overlayRef.current!.style.cursor = 'grab';
    drag.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* not captured */
    }
  };

  // ------------------------------------------------------------ palette drops
  useEffect(() =>
    registerDropTarget({
      over: (cx, cy, payload) => {
        const s = getState();
        const el = scrollRef.current;
        const rect = overlayRef.current?.getBoundingClientRect();
        let hit: DropHit | null = null;
        if (el && rect && s.mode === 'edit' && s.doc.canEdit && cx >= rect.left && cx <= rect.right && cy >= rect.top && cy <= rect.bottom) {
          // Scroll when the pointer nears the top or bottom edge.
          if (cy < rect.top + 36) el.scrollTop -= 14;
          else if (cy > rect.bottom - 36) el.scrollTop += 14;
          const p = clientToLayout(cx, cy);
          hit = dropTarget(layout, s.ed.score, p.x, p.y, s.ed.cursor.voice);
        }
        dropPreview.current = hit ? { hit, payload } : null;
        paintOverlay();
        return !!hit;
      },
      drop: (cx, cy, payload) => {
        dropPreview.current = null;
        const s = getState();
        const p = clientToLayout(cx, cy);
        const hit = dropTarget(layout, s.ed.score, p.x, p.y, s.ed.cursor.voice);
        if (!hit) return paintOverlay();
        scrollRef.current?.focus({ preventScroll: true });
        edit((ed) => placeNote(ed, { ref: hit.ref, lane: hit.lane, string: hit.string, staffPos: hit.staffPos }, payload), { label: payload.rest ? 'Place rest' : 'Place note' });
        paintOverlay();
      },
      leave: () => {
        dropPreview.current = null;
        paintOverlay();
      },
    }),
  );

  const onDoubleClick = (e: React.MouseEvent) => {
    // Double-click a bar: select the whole bar.
    const rect = overlayRef.current!.getBoundingClientRect();
    const { scrollTop, scrollLeft } = drawState.current;
    const x = e.clientX - rect.left - offsetX + scrollLeft;
    const y = e.clientY - rect.top + scrollTop;
    const s = getState();
    const hit = hitTest(layout, s.ed.score, x, y, s.ed.cursor.voice);
    if (!hit) return;
    const n = voiceOf(s.ed.score, hit.ref).length;
    edit((ed) => ({ ...ed, selection: { anchor: { bar: hit.ref.bar, beat: 0 }, focus: { bar: hit.ref.bar, beat: Math.max(0, n - 1) } } }), { select: true });
  };

  const contentW = layout.mode === 'horizontal' ? layout.width : Math.max(size.w, layout.width);
  return (
    <div
      className={`score-scroll layout-${layout.mode}`}
      ref={scrollRef}
      onScroll={onScroll}
      tabIndex={-1}
      aria-label="Score"
    >
      <div className="score-content" style={{ height: layout.height, width: contentW }}>
        <div className="score-canvases" style={{ width: size.w, height: size.h }}>
          <canvas ref={notationRef} className="score-canvas" style={{ width: size.w, height: size.h }} aria-hidden="true" />
          <canvas ref={loopRef} className="score-canvas" style={{ width: size.w, height: size.h }} aria-hidden="true" />
          <canvas
            ref={overlayRef}
            className="score-canvas score-overlay"
            style={{ width: size.w, height: size.h }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onDoubleClick={onDoubleClick}
            data-testid="score-overlay"
          />
        </div>
      </div>
    </div>
  );
}

function line(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

function drawSelection(ctx: CanvasRenderingContext2D, layout: Layout, ed: EditorState) {
  const refs = selectedRefs(ed);
  const sp = layout.sp;
  const bySystem = new Map<number, { x0: number; x1: number; tracks: Set<number> }>();
  const ids: string[] = [];
  for (const r of refs) {
    const b = voiceOf(ed.score, r)[r.beat];
    if (!b) continue;
    const box = layout.beatIndex.get(b.id);
    if (!box) continue;
    ids.push(b.id, ...b.notes.map((n) => n.id));
    const cur = bySystem.get(box.system) ?? { x0: Infinity, x1: -Infinity, tracks: new Set<number>() };
    cur.x0 = Math.min(cur.x0, box.x - 0.5 * sp);
    cur.x1 = Math.max(cur.x1, ed.selection ? box.xEnd - 0.4 * sp : box.x + box.w + 0.5 * sp);
    cur.tracks.add(r.track);
    bySystem.set(box.system, cur);
  }
  if (ed.selection) {
    for (const [si, r] of bySystem) {
      const sys = layout.systems[si];
      const bands = sys.bands.filter((b) => r.tracks.has(b.track));
      if (!bands.length) continue;
      const top = Math.min(...bands.map((b) => b.top)) - 1.5 * sp;
      const bottom = Math.max(...bands.map((b) => b.bottom)) + 1.5 * sp;
      ctx.fillStyle = SELECTION_BAND;
      ctx.beginPath();
      ctx.roundRect(r.x0, top, r.x1 - r.x0, bottom - top, 4);
      ctx.fill();
    }
  }
  for (const id of ids) paintIdItems(ctx, layout, id, ACCENT);
}

/** On-screen position of the cursor when it is in view (the zoom anchor for keys and buttons). */
function cursorAnchor(layout: Layout, offsetX: number, el: HTMLElement, scrollTop: number, scrollLeft: number): { x: number; y: number } | null {
  const ed = getState().ed;
  const box = boxForRef(layout, ed.score, ed.cursor);
  const sys = box && layout.systems[box.system];
  if (!box || !sys) return null;
  const x = box.x + offsetX - scrollLeft;
  const y = (sys.top + sys.bottom) / 2 - scrollTop;
  return x >= 0 && x <= el.clientWidth && y >= 0 && y <= el.clientHeight ? { x, y } : null;
}

/** Ghost of the note a palette drag would place: column band plus a fret "0", a notehead or a rest. */
function drawDropPreview(ctx: CanvasRenderingContext2D, layout: Layout, hit: DropHit, payload: NoteDragPayload) {
  const sp = layout.sp;
  const band = hit.band;
  const bands = hit.system.bands.filter((b) => b.track === band.track);
  const top = Math.min(...bands.map((b) => b.top)) - sp;
  const bottom = Math.max(...bands.map((b) => b.bottom)) + sp;
  ctx.save();
  ctx.fillStyle = 'rgba(239,106,76,0.10)';
  ctx.fillRect(hit.cx - 0.95 * sp, top, 1.9 * sp, bottom - top);
  ctx.strokeStyle = ACCENT;
  ctx.fillStyle = ACCENT;
  ctx.lineWidth = 1.2;
  if (hit.append) {
    // Sketch of the bar the drop adds: dashed lines and a closing barline.
    const x0 = hit.cx - 2 * sp;
    const x1 = hit.cx + 2 * sp;
    ctx.save();
    ctx.setLineDash([3, 3]);
    ctx.globalAlpha = 0.6;
    for (const b of bands) {
      for (let i = 0; i < b.lines; i++) line(ctx, x0, b.top + i * b.gap, x1, b.top + i * b.gap);
      line(ctx, x1, b.top, x1, b.bottom);
    }
    ctx.restore();
  }
  if (payload.rest) {
    ctx.setLineDash([3, 2]);
    ctx.strokeRect(hit.cx - 0.6 * sp, band.top + (band.bottom - band.top) / 2 - sp, 1.2 * sp, 2 * sp);
  } else if (band.kind === 'tab') {
    const y = band.top + (hit.string - 1) * band.gap;
    ctx.beginPath();
    ctx.roundRect(hit.cx - 0.75 * sp, y - 0.7 * sp, 1.5 * sp, 1.4 * sp, 0.35 * sp);
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = ACCENT;
    ctx.font = `bold ${(1.15 * sp).toFixed(1)}px Arial, Helvetica, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const b = voiceOf(getState().ed.score, hit.ref)[hit.ref.beat];
    const existing = hit.insert || !b || b.rest ? undefined : b.notes.find((n) => n.string === hit.string);
    ctx.fillText(String(existing?.fret ?? 0), hit.cx, y + 0.05 * sp);
  } else {
    const y = band.top + 2 * sp - hit.staffPos * (sp / 2);
    for (let p = 6; p <= hit.staffPos; p += 2) line(ctx, hit.cx - 0.9 * sp, band.top + 2 * sp - p * (sp / 2), hit.cx + 0.9 * sp, band.top + 2 * sp - p * (sp / 2));
    for (let p = -6; p >= hit.staffPos; p -= 2) line(ctx, hit.cx - 0.9 * sp, band.top + 2 * sp - p * (sp / 2), hit.cx + 0.9 * sp, band.top + 2 * sp - p * (sp / 2));
    ctx.beginPath();
    ctx.ellipse(hit.cx, y, 0.62 * sp, 0.45 * sp, -0.35, 0, Math.PI * 2);
    if ((payload.base ?? 4) >= 4) ctx.fill();
    else ctx.stroke();
  }
  ctx.restore();
}

function paintIdItems(ctx: CanvasRenderingContext2D, layout: Layout, id: string, color: string) {
  const entries = layout.itemsById.get(id);
  if (!entries) return;
  for (const e of entries) {
    ctx.save();
    ctx.translate(0, e.sys.dy);
    for (const it of e.items) paintItem(ctx, it, { color });
    ctx.restore();
  }
}

function playheadPos(layout: Layout, tick: number): { x: number; system: number; bar: BarBox } | null {
  const tl = getState().timeline;
  if (!tl.bars.length) return null;
  const pi = playedBarAt(tl, tick);
  const pb = tl.bars[pi];
  const bar = layout.bars.find((b) => b.bar === pb.barIdx);
  if (!bar) return null;
  return { x: xAtTick(bar, tick - pb.startTick), system: bar.system, bar };
}

function drawPlayhead(ctx: CanvasRenderingContext2D, layout: Layout, tick: number, playing: boolean) {
  const pos = playheadPos(layout, tick);
  if (!pos) return;
  const sys = layout.systems[pos.system];
  const sp = layout.sp;
  ctx.strokeStyle = PLAYHEAD;
  ctx.lineWidth = Math.max(2, 0.25 * sp);
  ctx.lineCap = 'round';
  line(ctx, pos.x, sys.top - 1.5 * sp, pos.x, sys.bottom + 1.5 * sp);
  if (!playing) return;
  const tl = getState().timeline;
  for (const ev of eventsAt(tl, tick)) paintIdItems(ctx, layout, ev.noteId, PLAYING);
}

function loopRect(layout: Layout, fromBar: number, toBar: number) {
  const boxes = layout.bars.filter((b) => b.bar >= fromBar && b.bar <= toBar);
  return boxes;
}

function drawLoop(ctx: CanvasRenderingContext2D, layout: Layout, fromBar: number, toBar: number, _w: number, scrollTop: number, h: number) {
  const boxes = loopRect(layout, fromBar, toBar);
  if (!boxes.length) return;
  const sp = layout.sp;
  // Dim everything outside the loop.
  ctx.fillStyle = 'rgba(30,30,36,0.12)';
  for (const sys of layout.systems) {
    if (sys.y1 < scrollTop - 50 || sys.y0 > scrollTop + h + 50) continue;
    const inSys = boxes.filter((b) => b.system === sys.index);
    const top = sys.top - 2 * sp;
    const bottom = sys.bottom + 2 * sp;
    if (!inSys.length) {
      ctx.fillRect(sys.x0, top, sys.x1 - sys.x0 + 4, bottom - top);
      continue;
    }
    const a = inSys[0].x0;
    const b = inSys[inSys.length - 1].x1;
    ctx.fillRect(sys.x0, top, a - sys.x0, bottom - top);
    ctx.fillRect(b, top, sys.x1 - b + 4, bottom - top);
  }
  // Region outline + round-ended handles.
  const first = boxes[0];
  const last = boxes[boxes.length - 1];
  for (const sysIdx of new Set(boxes.map((b) => b.system))) {
    const sys = layout.systems[sysIdx];
    const inSys = boxes.filter((b) => b.system === sysIdx);
    const x0 = inSys[0].x0;
    const x1 = inSys[inSys.length - 1].x1;
    const top = sys.top - 2 * sp;
    const bottom = sys.bottom + 2 * sp;
    ctx.strokeStyle = PLAYHEAD;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x0, top);
    ctx.lineTo(x1, top);
    ctx.stroke();
    for (const [x, isHandle] of [
      [x0, inSys[0] === first],
      [x1, inSys[inSys.length - 1] === last],
    ] as const) {
      if (!isHandle) continue;
      ctx.fillStyle = PLAYHEAD;
      ctx.beginPath();
      ctx.roundRect(x - 3, top - 2, 6, bottom - top + 4, 3);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, top - 6, 6, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function loopHandleAt(layout: Layout, fromBar: number, toBar: number, x: number, y: number): 'loopStart' | 'loopEnd' | null {
  const boxes = loopRect(layout, fromBar, toBar);
  if (!boxes.length) return null;
  const first = boxes[0];
  const last = boxes[boxes.length - 1];
  const sp = layout.sp;
  const inSys = (b: BarBox) => {
    const sys = layout.systems[b.system];
    return y >= sys.top - 3 * sp && y <= sys.bottom + 2 * sp;
  };
  if (inSys(first) && Math.abs(x - first.x0) < 8) return 'loopStart';
  if (inSys(last) && Math.abs(x - last.x1) < 8) return 'loopEnd';
  return null;
}
